// Import React components
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Row, Col, Card, Image, Container } from "react-bootstrap";

// Import global stylesheet & images
import "../interfaceSettings.css";
import pic1 from "../assets/webp/GGCWalking01.webp";
import pic2 from "../assets/webp/bodyweight1.webp";
import pic3 from "../assets/webp/GGCWalking02.webp";
import pic4 from "../assets/webp/stretch1.webp";
import pic5 from "../assets/webp/injury1.webp";
import pic6 from "../assets/webp/hydration1.webp";

// Import User settings
import UserInfoForm from "../components/Form/UserInfoForm.js";

// Main Component
const UserHome = () => {
  const [username, setUserName] = useState("");

  useEffect(() => {
    const storedProfile = localStorage.getItem("profile");
    if (storedProfile) {
      try {
        const { username } = JSON.parse(storedProfile);
        setUserName(username || "User");
      } catch (error) {
        console.error("Error parsing profile:", error);
        setUserName("User");
      }
    } else {
      setUserName("User");
    }
  }, []);

  return (
    <main className="container-xl" role="main">
      {/* Welcome Section */}
      <section id="user" aria-labelledby="welcome-heading">
        <Container className="body-custom my-5 p-2 rounded border">
          <div className="container mt-5">
            <Card className="shadow-sm" aria-labelledby="welcome-heading">
              <h1 id="welcome-heading">Welcome Back, {username}</h1>
            </Card>
          </div>
        </Container>
      </section>

      <Container className="body-custom my-5 p-2 rounded border">

        {/* Walking Trails */}
        <section id="showcase" aria-labelledby="trails-heading">
          <div className="container my-4">
            <Card className="shadow-sm">
              <Row className="align-items-center">
                <Col md={6} className="text-center">
                  <Image
                    src={pic1}
                    alt="Walking Path sign with trail routes and QR code, while students walk on campus in the background"
                    className="img-fluid rounded mx-auto"
                  />
                </Col>
                <Col md={6} className="p-3 p-md-5">
                  <h2 id="trails-heading">Walking Trails</h2>
                  <p className="lead my-4 text-center">
                    Discover scenic walking trails around campus designed to keep you active and engaged.
                  </p>
                  <Link
                    to="/trails"
                    className="btn btn-primary mt-3 btn-responsive btn-block"
                    aria-label="View campus walking trails"
                  >
                    <i className="bi bi-chevron-right" aria-hidden="true"></i>{" "}
                    <span>Trails</span>
                  </Link>
                </Col>
              </Row>
            </Card>
          </div>
        </section>

        {/* Body Weight Exercises */}
        <section id="exercise" aria-labelledby="exercise-heading">
          <div className="container my-4">
            <Card className="shadow-sm">
              <Row className="align-items-center">
                <Col md={6} className="text-center">
                  <Image
                    src={pic2}
                    alt="Person in red exercise jacketsquatting outdoors for exercise"
                    className="img-fluid rounded mx-auto"
                  />
                </Col>
                <Col md={6} className="p-3 p-md-5">
                  <h2 id="exercise-heading">Body Weight Exercises</h2>
                  <p className="lead text-center">
                    Try a variety of body-weight exercises to build strength and endurance.
                  </p>
                  <Link
                    to="/bodyWeightExercises"
                    className="btn btn-primary mt-3 btn-responsive btn-block"
                    aria-label="Explore body weight exercises"
                  >
                    <i className="bi bi-chevron-right" aria-hidden="true"></i>{" "}
                    <span>Exercises</span>
                  </Link>
                </Col>
              </Row>
            </Card>
          </div>
        </section>

        {/* Proper Walking Techniques */}
        <section id="properWalkingTechniques" aria-labelledby="techniques-heading">
          <div className="container my-4">
            <Card className="shadow-sm">
              <Row className="align-items-center">
                <Col md={6} className="text-center">
                  <Image
                    src={pic3}
                    alt="Person in blue shirt walking outdoors demonstrating proper walking technique"
                    className="img-fluid rounded mx-auto"
                  />
                </Col>
                <Col md={6} className="p-3 p-md-5">
                  <h2 id="techniques-heading">Proper Walking Techniques</h2>
                  <p className="lead text-center">
                    Learn techniques to improve your walking posture and efficiency.
                  </p>
                  <Link
                    to="/properWalkingTechniques"
                    className="btn btn-primary mt-3 btn-responsive btn-block"
                    aria-label="Learn proper walking techniques"
                  >
                    <i className="bi bi-chevron-right" aria-hidden="true"></i>{" "}
                    <span>Proper Walking Techniques</span>
                  </Link>
                </Col>
              </Row>
            </Card>
          </div>
        </section>

        {/* Stretches For Walking */}
        <section id="stretchesForWalking" aria-labelledby="stretches-heading">
          <div className="container my-4">
            <Card className="shadow-sm">
              <Row className="align-items-center">
                <Col md={6} className="text-center">
                  <Image
                    src={pic4}
                    alt="Person in purple exercise jacketstretching their leg muscles outdoors"
                    className="img-fluid rounded mx-auto"
                  />
                </Col>
                <Col md={6} className="p-3 p-md-5">
                  <h2 id="stretches-heading">Stretches For Walking</h2>
                  <p className="lead text-center">
                    Enhance flexibility and prevent injuries with these essential stretches.
                  </p>
                  <Link
                    to="/stretchesForWalking"
                    className="btn btn-primary mt-3 btn-responsive btn-block"
                    aria-label="View walking stretch routines"
                  >
                    <i className="bi bi-chevron-right" aria-hidden="true"></i>{" "}
                    <span>Stretches For Walking</span>
                  </Link>
                </Col>
              </Row>
            </Card>
          </div>
        </section>

        {/* Nutrition and Hydration */}
        <section id="nutritionAndHydration" aria-labelledby="nutrition-heading">
          <div className="container my-4">
            <Card className="shadow-sm">
              <Row className="align-items-center">
                <Col md={6} className="text-center">
                  <Image
                    src={pic6}
                    alt="Sliced fruits in clear cups of water with striped straws"
                    className="img-fluid rounded mx-auto"
                  />
                </Col>
                <Col md={6} className="p-3 p-md-5">
                  <h2 id="nutrition-heading">Nutrition and Hydration</h2>
                  <p className="lead text-center">
                    Fuel your body with the right nutrients and stay hydrated for better performance.
                  </p>
                  <Link
                    to="/nutritionAndHydration"
                    className="btn btn-primary mt-3 btn-responsive btn-block"
                    aria-label="Read about nutrition and hydration tips"
                  >
                    <i className="bi bi-chevron-right" aria-hidden="true"></i>{" "}
                    <span>Nutrition and Hydration</span>
                  </Link>
                </Col>
              </Row>
            </Card>
          </div>
        </section>

        {/* Injury Prevention */}
        <section id="injuryPrevention" aria-labelledby="injury-heading">
          <div className="container my-4">
            <Card className="shadow-sm">
              <Row className="align-items-center">
                <Col md={6} className="text-center">
                  <Image
                    src={pic5}
                    alt="First aid kit with bandages and medical supplies opened"
                    className="img-fluid rounded mx-auto"
                  />
                </Col>
                <Col md={6} className="p-3 p-md-5">
                  <h2 id="injury-heading">Injury Prevention</h2>
                  <p className="lead text-center">
                    Learn tips and techniques to prevent injuries and stay active.
                  </p>
                  <Link
                    to="/injuryPrevention"
                    className="btn btn-primary mt-3 btn-responsive btn-block"
                    aria-label="Explore injury prevention tips"
                  >
                    <i className="bi bi-chevron-right" aria-hidden="true"></i>{" "}
                    <span>Injury Prevention</span>
                  </Link>
                </Col>
              </Row>
            </Card>
          </div>
        </section>

      </Container>
    </main>
  );
};

export default UserHome;
